import { useCallback, useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"
import {
  deleteStudentNote,
  listStudentNotes,
} from "../../../../api/studentNotes"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import type { PaginationMeta } from "../../Questions/types"
import { NOTES_PAGE_SIZE, type StudentNote } from "../types"
import { mapNoteCard } from "../utils/mapNoteCard"

const mergeNotes = (current: StudentNote[], incoming: StudentNote[]) => {
  const seen = new Set(current.map((item) => item.id))
  const extra = incoming.filter((item) => !seen.has(item.id))
  return extra.length === 0 ? current : [...current, ...extra]
}

const useNotes = () => {
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<StudentNote[]>([])
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState("")
  const [removingId, setRemovingId] = useState<string | null>(null)
  const requestId = useRef(0)
  const removingRef = useRef(false)
  const pageRef = useRef(1)

  const fetchPage = useCallback(
    async (pageNumber: number, mode: "replace" | "append" | "window") => {
      const id = ++requestId.current
      if (mode === "append") setLoadingMore(true)
      else setLoading(true)
      setError("")
      const perPage =
        mode === "window"
          ? Math.min(100, Math.max(NOTES_PAGE_SIZE, pageRef.current * NOTES_PAGE_SIZE))
          : NOTES_PAGE_SIZE
      const requestPage = mode === "window" ? 1 : pageNumber
      try {
        const res = await listStudentNotes(requestPage, perPage)
        if (id !== requestId.current) return
        if (!res.data?.success) {
          throw new Error(res.data?.message || "Failed to load notes")
        }
        const nextItems = Array.isArray(res.data.data) ? res.data.data : []
        if (mode === "append") {
          setItems((prev) => mergeNotes(prev, nextItems))
        } else {
          setItems(nextItems)
        }
        setPagination(res.data.pagination ?? null)
        if (mode !== "window") {
          pageRef.current = pageNumber
          setPage(pageNumber)
        }
      } catch (err) {
        if (id !== requestId.current) return
        if (mode !== "append") {
          setItems([])
          setPagination(null)
        }
        setError(getLearnerApiErrorMessage(err, "Failed to load notes"))
      } finally {
        if (id === requestId.current) {
          setLoading(false)
          setLoadingMore(false)
          setHasLoaded(true)
        }
      }
    },
    []
  )

  useEffect(() => {
    void fetchPage(1, "replace")
  }, [fetchPage])

  const afterCreate = useCallback(async () => {
    pageRef.current = 1
    setPage(1)
    await fetchPage(1, "replace")
  }, [fetchPage])

  const afterUpdate = useCallback(async () => {
    await fetchPage(1, "window")
  }, [fetchPage])

  const loadMore = useCallback(async () => {
    if (loading || loadingMore) return
    const total = pagination?.total ?? 0
    if (items.length >= total) return
    await fetchPage(page + 1, "append")
  }, [fetchPage, items.length, loading, loadingMore, page, pagination?.total])

  const removeNote = useCallback(
    async (noteId: string) => {
      if (removingRef.current) return false
      removingRef.current = true
      const snapshot = items
      const snapshotPagination = pagination
      setRemovingId(noteId)
      setItems((prev) => prev.filter((item) => item.id !== noteId))
      setPagination((prev) =>
        prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev
      )
      try {
        await deleteStudentNote(noteId)
        toast.success("Note deleted")
        await fetchPage(1, "window")
        return true
      } catch (err) {
        setItems(snapshot)
        setPagination(snapshotPagination)
        toast.error(getLearnerApiErrorMessage(err, "Could not delete note"))
        return false
      } finally {
        removingRef.current = false
        setRemovingId(null)
      }
    },
    [fetchPage, items, pagination]
  )

  const total = pagination?.total ?? 0
  const hasMore = items.length < total

  return {
    cards: items.map(mapNoteCard),
    pagination,
    loading,
    loadingMore,
    hasLoaded,
    hasMore,
    error,
    loadMore,
    afterCreate,
    afterUpdate,
    removeNote,
    removingId,
  }
}

export default useNotes
